import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CloudArrowUp, FileCsv, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import ChecadoresCHKR from "@/components/ChecadoresCHKR";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_checadores_seen";

const TOUR_STEPS = {
  chkr: [
    { target: "[data-tour='checadores-nuevo']", title: "Registra un checador", content: "Da de alta cada checador de escritorio CHKR: eliges su empresa (y, si quieres, el centro donde está) y obtienes el bloque de credenciales para su config.json." },
    { target: "[data-tour='checadores-tabla']", title: "Estado de cada checador", content: "Aquí ves cuándo envió marcas por última vez y si la nube rechazó alguna. Un checador dado de baja ya no puede enviar marcas." },
  ],
  csv: [
    { target: "[data-tour='csv-dropzone']", title: "Sube el archivo del checador", content: "Selecciona o arrastra el CSV exportado de tu checador biométrico con las marcaciones del día." },
    { target: "[data-tour='csv-formato']", title: "Formato requerido", content: "El CSV debe traer biometric_id, fecha (YYYY-MM-DD), hora (HH:MM:SS) y tipo (entrada o salida). Los empleados sin biometric_id se reportan como error." },
  ],
};

/**
 * CSV format expected:
 * biometric_id,fecha,hora,tipo
 * BIO001,2026-05-08,09:01:23,entrada
 * BIO001,2026-05-08,18:02:11,salida
 */
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = (k) => headers.indexOf(k);
  const out = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",");
    out.push({
      biometric_id: cols[idx("biometric_id")]?.trim(),
      fecha: cols[idx("fecha")]?.trim(),
      hora: cols[idx("hora")]?.trim() || "00:00:00",
      tipo: (cols[idx("tipo")] || "entrada").trim().toLowerCase(),
    });
  }
  return out;
}

export default function Checadores() {
  const [text, setText] = useState("");
  const [registros, setRegistros] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("chkr");
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      const txt = String(r.result || "");
      setText(txt);
      setRegistros(parseCSV(txt));
    };
    r.readAsText(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/jornadas/import", { registros });
      setResult(data);
      toast.success(`${data.importados} registros importados`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="checadores-page">
      <PageHeader
        kicker="Sincronización biométrica"
        title="Checadores"
        subtitle="Registra los checadores CHKR que envían sus marcas a la nube, o importa registros de otros checadores biométricos vía CSV."
        actions={<TourHelpButton onClick={startTour} />}
      />

      <GuidedTour steps={TOUR_STEPS[tab]} active={tourActive} onFinish={finishTour} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="chkr" data-testid="checadores-tab-chkr">Checadores CHKR</TabsTrigger>
          <TabsTrigger value="csv" data-testid="checadores-tab-csv">Importar CSV</TabsTrigger>
        </TabsList>

        <TabsContent value="chkr">
          <ChecadoresCHKR />
        </TabsContent>

        <TabsContent value="csv">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <label className="block">
            <div className="border-2 border-dashed border-slate-300 rounded-md p-12 text-center bg-slate-50 hover:bg-slate-100 cursor-pointer transition" data-testid="csv-dropzone" data-tour="csv-dropzone">
              <CloudArrowUp size={48} weight="duotone" className="mx-auto text-slate-400 mb-3" />
              <div className="font-display font-bold text-ink">Selecciona archivo CSV</div>
              <div className="text-sm text-slate-500 mt-1">o arrástralo aquí</div>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-input" />
            </div>
          </label>

          {registros.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <FileCsv size={20} weight="duotone" className="text-slate-700" />
                  <span className="font-semibold text-sm">Vista previa: {registros.length} filas</span>
                </div>
                <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="csv-import-btn">
                  {busy ? "Importando…" : "Importar registros"}
                </Button>
              </div>
              <div className="max-h-96 overflow-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Biometric ID</th>
                      <th>Fecha</th>
                      <th>Hora</th>
                      <th>Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registros.slice(0, 200).map((r, i) => (
                      <tr key={i}>
                        <td className="font-mono text-xs">{r.biometric_id}</td>
                        <td className="font-mono text-xs">{r.fecha}</td>
                        <td className="font-mono text-xs">{r.hora}</td>
                        <td><span className="text-xs uppercase tracking-wider font-bold">{r.tipo}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={22} weight="duotone" className="text-emerald-600" />
                <h3 className="font-display font-bold text-lg">Resultado de importación</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="border border-slate-200 rounded p-3">
                  <div className="label-uppercase">Importados</div>
                  <div className="font-display text-3xl font-black text-emerald-700">{result.importados}</div>
                </div>
                <div className="border border-slate-200 rounded p-3">
                  <div className="label-uppercase">Errores</div>
                  <div className="font-display text-3xl font-black text-red-600">{result.errores?.length || 0}</div>
                </div>
              </div>
              {result.errores?.length > 0 && (
                <div className="space-y-1 max-h-40 overflow-y-auto text-xs">
                  {result.errores.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-red-700 bg-red-50 p-2 rounded">
                      <Warning size={14} weight="bold" className="mt-0.5" />
                      <span>{e.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-5" data-tour="csv-formato">
          <div className="label-uppercase mb-3">Formato esperado</div>
          <pre className="font-mono text-xs bg-white border border-slate-200 rounded p-3 overflow-x-auto">{`biometric_id,fecha,hora,tipo
BIOEMP001,2026-05-08,09:01:23,entrada
BIOEMP001,2026-05-08,18:02:11,salida
BIOEMP002,2026-05-08,09:15:00,entrada`}</pre>
          <ul className="mt-4 text-xs text-slate-600 space-y-2">
            <li>• <strong>biometric_id</strong>: ID asignado al empleado en el checador.</li>
            <li>• <strong>fecha</strong>: YYYY-MM-DD.</li>
            <li>• <strong>hora</strong>: HH:MM:SS.</li>
            <li>• <strong>tipo</strong>: entrada o salida.</li>
            <li>• Empleados sin biometric_id registrado serán reportados como error.</li>
          </ul>
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
